import {
    animate,
    AnimationBuilder,
    AnimationFactory,
    AnimationPlayer,
    style,
} from '@angular/animations';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    ElementRef,
    HostListener,
    Input,
    QueryList,
    ViewChild,
    ViewChildren,
} from '@angular/core';

import { CarouselItemDirective } from './carousel-item.directive';
import { CarouselItemElementDirective } from './carousel-item-element.directive';

@Component({
    // tslint:disable-next-line:component-selector
    selector: 'carousel',
    exportAs: 'carousel',
    templateUrl: './carousel.component.html',
    styleUrls: ['./carousel.component.scss'],
})
export class CarouselComponent implements AfterViewInit {
    setSlides = 4;
    prevSetSlides = 4;

    @ContentChildren(CarouselItemDirective)
    items: QueryList<CarouselItemDirective>;

    @ViewChildren(CarouselItemElementDirective, { read: ElementRef })
    private itemsElements: QueryList<ElementRef>;

    @ViewChild('carousel') private carousel: ElementRef;
    @Input() timing = '250ms ease-in';
    @Input() showControls = true;
    private player: AnimationPlayer;
    private carouselWidth: number;
    private currentSlide = 0;
    carouselWrapperStyle = {};
    carouselItemStyle = {};

    constructor(private builder: AnimationBuilder, private cdr: ChangeDetectorRef) { }
    /**
     * Listens for changes to the viewport size and triggers a re-sizing of the carousel.
     */
    @HostListener('window:resize', ['$event'])
    onResize(event) {
        const value = event.target.innerWidth;
        this.setSlidesByScreenSize(value);

        if (this.setSlides !== this.prevSetSlides) this.resizeCarousel(value);
    }

    ngAfterViewInit() {
        const value = window.innerWidth;
        this.setSlidesByScreenSize(value);

        this.resizeCarousel(value);
        this.cdr.detectChanges();
    }
    
    setSlidesByScreenSize(value): void {
        if (value < 600) { this.setSlides = 1; }
        else if (value >= 600 && value < 1023) { this.setSlides = 2; }
        else if (value >= 1023 && value < 1439) { this.setSlides = 3; }
        else if (value >= 1439 && value < 1919) { this.setSlides = 4; }
        else { this.setSlides = 4; this.prevSetSlides = 4 }
    }

    /**
     * Progresses the carousel forward by x amount of slides, depending on screen size.
     */
    next() {
        // this.currentSlide = (this.currentSlide + 1) % this.items.length; // original, by 1 slide at a time
        const slidevalue = (this.currentSlide + this.setSlides) % (this.items.length);

        if (this.prevSetSlides > slidevalue) {
            this.currentSlide = 0;
        }
        else {
            this.currentSlide = slidevalue;
        }

        this.transitionCarousel();
    }

    /**
     * Regresses the carousel backwards by x amount of slides, depending on screen size.
     */
    prev() {
        // this.currentSlide = (this.currentSlide - 1 + this.items.length) % this.items.length; // original, by 1 slide at a time
        const slidevalue = (this.currentSlide - this.setSlides + this.items.length) % this.items.length;

        if (this.currentSlide < this.prevSetSlides && this.currentSlide > 0) {
            this.currentSlide = 0;
        }
        else {
            this.currentSlide = slidevalue;
        }

        this.transitionCarousel();
    }

    /**
     * Re-sizes the carousel container and triggers `this.transitionCarousel()` to reset the childrens' positions.
     *
     * For use on initial load, and when changing viewport size.
     */
    resizeCarousel(screenSize: number): void {
        // re-size the container
        const defaultWidth = 350;
        const gap = 16;
        const wrap = screenSize < 1023 ? 'wrap' : 'nowrap';
        // console.log(this.itemsElements?.first?.nativeElement);
        this.carouselWidth = ((this.itemsElements?.first?.nativeElement?.getBoundingClientRect()?.width || defaultWidth) + gap) * this.setSlides;
        this.carouselWrapperStyle = {
            'width': `${this.carouselWidth}px`,
            'flex-wrap': `${wrap}`,
        };
        this.carouselItemStyle = {
            'margin-right': `${gap}px`,
            'margin-bottom': `${gap}px`,
        }

        // trigger a fresh transition to the current slide to reset the position of the children
        this.transitionCarousel();
        this.prevSetSlides = this.setSlides;
    }

    /**
     * Animates the carousel to the currently selected slide.
     *
     * **You must set `this.currentSlide` before calling this method, or it will have no effect.**
     */
    transitionCarousel() {
        // const offset = this.currentSlide * this.carouselWidth;
        const offset = (this.currentSlide * this.carouselWidth) / this.setSlides;
        const myAnimation: AnimationFactory = this.buildAnimation(offset);
        this.player = myAnimation.create(this.carousel.nativeElement);
        this.player.play();
    }

    private buildAnimation(offset) {
        return this.builder.build([
            animate(this.timing, style({ transform: `translateX(-${offset}px)` })),
        ]);
    }
}
